import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUniAI } from '../hooks/useUniAI';
import { Mic, Square, Sparkles, X, Send, Volume2, VolumeX, Loader2, Maximize2, User, Radio } from 'lucide-react';
import { Input } from './ui/input';

export const UniAIWidget = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const { messages, recording, processing, speaking, muted, setMuted, conversationMode, toggleConversationMode, toggleRecording, sendMessage } = useUniAI();

  // Hide the floating widget on the dedicated Uni AI page and on login
  if (location.pathname === '/uni-ai' || location.pathname === '/login') return null;

  const handleSend = (e) => {
    e?.preventDefault();
    if (!text.trim()) return;
    sendMessage(text.trim());
    setText('');
  };

  const recent = messages.slice(-6);

  return (
    <>
      {/* Floating mic button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full bg-gradient-to-br from-[#F5A623] to-[#D48C1B] text-black shadow-xl shadow-[#F5A623]/40 flex items-center justify-center hover:scale-105 transition-transform"
          data-testid="uni-ai-fab"
          title="Ask Uni AI"
        >
          <Sparkles className="h-6 w-6" />
        </button>
      )}

      {/* Mini panel */}
      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-[360px] max-w-[calc(100vw-2rem)] rounded-2xl border border-border/70 bg-card shadow-2xl flex flex-col overflow-hidden animate-in slide-in-from-bottom-4 duration-200" data-testid="uni-ai-widget">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-[#F5A623]/15 to-transparent border-b border-border/60">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-xl bg-gradient-to-br from-[#F5A623] to-[#D48C1B] flex items-center justify-center">
                <Sparkles className="h-4 w-4 text-black" />
              </div>
              <div>
                <p className="text-sm font-semibold leading-none">Uni AI</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">{recording ? 'Listening…' : processing ? 'Thinking…' : speaking ? 'Speaking…' : 'Ready'}</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={toggleConversationMode} className={`h-7 w-7 rounded-md flex items-center justify-center ${conversationMode ? 'bg-[#F5A623] text-black' : 'hover:bg-accent'}`} data-testid="uni-ai-widget-convo" title="Hands-free conversation">
                <Radio className={`h-4 w-4 ${conversationMode ? 'animate-pulse' : ''}`} />
              </button>
              <button onClick={() => setMuted(!muted)} className="h-7 w-7 rounded-md hover:bg-accent flex items-center justify-center" data-testid="uni-ai-widget-mute" title={muted ? 'Unmute' : 'Mute'}>
                {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4 text-[#F5A623]" />}
              </button>
              <button onClick={() => { setOpen(false); navigate('/uni-ai'); }} className="h-7 w-7 rounded-md hover:bg-accent flex items-center justify-center" data-testid="uni-ai-widget-expand" title="Open full assistant">
                <Maximize2 className="h-4 w-4" />
              </button>
              <button onClick={() => setOpen(false)} className="h-7 w-7 rounded-md hover:bg-accent flex items-center justify-center" data-testid="uni-ai-widget-close" title="Close">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="h-64 overflow-y-auto p-3 space-y-3 text-sm">
            {recent.length === 0 && !processing && (
              <div className="h-full flex flex-col items-center justify-center text-center text-muted-foreground gap-2">
                <Mic className="h-6 w-6 text-[#F5A623]" />
                <p className="text-xs px-4">Tap the mic and say something like<br/>"What's overdue?" or "Open Analytics".</p>
              </div>
            )}
            {recent.map((m, i) => (
              <div key={i} className={`flex gap-2 ${m.role === 'user' ? 'flex-row-reverse' : ''}`}>
                <div className={`h-6 w-6 rounded-full flex items-center justify-center flex-shrink-0 ${m.role === 'user' ? 'bg-blue-500/15 text-blue-500' : 'bg-[#F5A623]/15 text-[#F5A623]'}`}>
                  {m.role === 'user' ? <User className="h-3 w-3" /> : <Sparkles className="h-3 w-3" />}
                </div>
                <div className={`max-w-[80%] rounded-xl px-3 py-1.5 text-xs leading-relaxed ${m.role === 'user' ? 'bg-blue-500/10' : 'bg-accent'}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {processing && (
              <div className="flex gap-2">
                <div className="h-6 w-6 rounded-full bg-[#F5A623]/15 text-[#F5A623] flex items-center justify-center"><Sparkles className="h-3 w-3" /></div>
                <div className="bg-accent rounded-xl px-3 py-2 flex items-center gap-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#F5A623] animate-bounce" />
                  <span className="h-1.5 w-1.5 rounded-full bg-[#F5A623] animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="h-1.5 w-1.5 rounded-full bg-[#F5A623] animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            )}
          </div>

          {/* Composer */}
          <form onSubmit={handleSend} className="flex items-center gap-2 p-3 border-t border-border/60">
            <button
              type="button"
              onClick={toggleRecording}
              disabled={processing}
              className={`relative h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${recording ? 'bg-red-500 text-white' : 'bg-[#F5A623] text-black hover:bg-[#D48C1B]'} disabled:opacity-50`}
              data-testid="uni-ai-widget-mic"
            >
              {recording && <span className="absolute inset-0 rounded-full bg-red-500 animate-ping opacity-40" />}
              {recording ? <Square className="h-4 w-4 relative" /> : <Mic className="h-4 w-4 relative" />}
            </button>
            <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Type or speak…" disabled={recording} className="flex-1 h-9 text-sm" data-testid="uni-ai-widget-input" />
            <button type="submit" disabled={processing || !text.trim()} className="h-9 w-9 rounded-lg bg-[#F5A623] text-black flex items-center justify-center disabled:opacity-40 flex-shrink-0" data-testid="uni-ai-widget-send">
              {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default UniAIWidget;
